import { isMfaError, staffErrorMessage } from "@/components/admin/StaffAccessNotice";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useState } from "react";

import { getBookingReports, exportBookingsCSV } from "@/lib/booking-ops.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const reportsQuery = (from?: string, to?: string) =>
  queryOptions({
    queryKey: ["admin", "reports", from ?? "", to ?? ""],
    queryFn: () => getBookingReports({ data: { from, to } }),
    staleTime: 30_000,
  });

export const Route = createFileRoute("/_authenticated/admin/reports")({
  head: () => ({
    meta: [{ title: "Reportes — Admin" }, { name: "robots", content: "noindex,nofollow" }],
  }),
  component: ReportsPage,
});

function ReportsPage() {
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  function downloadCsv() {
    exportBookingsCSV({ data: { from: from || undefined, to: to || undefined } }).then((res) => {
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reservas-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch((e) => {
      const msg = staffErrorMessage(e);
      if (isMfaError(e) && window.confirm(`${msg}\n\n¿Ir a la verificación en dos pasos?`)) {
        window.location.href = "/seguridad/mfa";
      } else if (!isMfaError(e)) {
        window.alert(msg);
      }
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <h1 className="font-display text-4xl font-semibold">Reportes operativos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ingresos, comisiones y desempeño por tour, proveedor y canal.
          </p>
        </div>
        <AdminNav />
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
          <div>
            <label className="text-xs text-muted-foreground">Desde</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-44" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Hasta</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-44" />
          </div>
          <Button variant="outline" size="sm" className="ml-auto" onClick={downloadCsv}>
            Exportar CSV
          </Button>
        </div>
        <Suspense fallback={<Skeleton className="h-72 w-full" />}>
          <ReportsBody from={from} to={to} />
        </Suspense>
      </div>
    </div>
  );
}

function ReportsBody({ from, to }: { from: string; to: string }) {
  const { data } = useSuspenseQuery(reportsQuery(from || undefined, to || undefined));
  const fmt = (n: number) => n.toLocaleString("es-MX", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Ingresos" value={`$${fmt(data.totals.revenue)}`} />
        <Stat label="Comisiones" value={`$${fmt(data.totals.commission)}`} />
        <Stat label="Reservas" value={fmt(data.totals.bookings)} />
        <Stat label="Pasajeros" value={fmt(data.totals.pax)} />
      </div>

      <Section title="Por mes">
        <Table
          rows={data.byMonth}
          cols={[
            { k: "key", label: "Mes" },
            { k: "revenue", label: "Ingresos", render: (v) => `$${fmt(Number(v))}` },
            { k: "bookings", label: "Reservas" },
            { k: "pax", label: "Pax" },
          ]}
        />
      </Section>

      <Section title="Top tours">
        <Table
          rows={data.byTour}
          cols={[
            { k: "key", label: "Tour" },
            { k: "revenue", label: "Ingresos", render: (v) => `$${fmt(Number(v))}` },
            { k: "bookings", label: "Reservas" },
            { k: "pax", label: "Pax" },
          ]}
        />
      </Section>

      <Section title="Por proveedor">
        <Table
          rows={data.byProvider}
          cols={[
            { k: "key", label: "Proveedor" },
            { k: "revenue", label: "Ingresos", render: (v) => `$${fmt(Number(v))}` },
            { k: "commission", label: "Comisión", render: (v) => `$${fmt(Number(v))}` },
            { k: "bookings", label: "Reservas" },
          ]}
        />
      </Section>

      <Section title="Por canal">
        <Table
          rows={data.bySource}
          cols={[
            { k: "key", label: "Canal" },
            { k: "revenue", label: "Ingresos", render: (v) => `$${fmt(Number(v))}` },
            { k: "bookings", label: "Reservas" },
          ]}
        />
      </Section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      <div className="overflow-hidden rounded-lg border bg-card">{children}</div>
    </div>
  );
}

function Table({
  rows,
  cols,
}: {
  rows: Array<Record<string, unknown>>;
  cols: Array<{ k: string; label: string; render?: (v: unknown) => string }>;
}) {
  if (!rows.length) {
    return <p className="p-6 text-center text-sm text-muted-foreground">Sin datos.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
        <tr>
          {cols.map((c) => (
            <th key={c.k} className="px-3 py-2">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t">
            {cols.map((c) => (
              <td key={c.k} className="px-3 py-2">
                {c.render ? c.render(r[c.k]) : String(r[c.k] ?? "—")}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
